import { useEffect, useRef, useState } from "react";

// Wire-format event from the agent's /api/v1/ws stream. Shape mirrors the
// Go ws.Event struct.
export interface WSEvent {
  kind: string;
  sourceId?: string;
  runId?: string;
  level?: string;
  message?: string;
  status?: string;
  at: string;
  data?: unknown;
}

interface UseWSOptions {
  // When set, the server filters the stream to events for this source only.
  // Absent / empty = "every event" — used by the dashboard ticker.
  sourceId?: string;
  // Called for every event the connection delivers. Useful for "react to
  // each event" logic (invalidating queries, flashing rows). The hook also
  // returns lastEvent for "give me the most recent" style consumers.
  onEvent?: (ev: WSEvent) => void;
}

interface UseWSResult {
  lastEvent: WSEvent | null;
  connected: boolean;
}

// useWS opens a WebSocket to the agent's /api/v1/ws endpoint and yields
// every event the server sends. Reconnects with exponential backoff capped
// at 30s. Closes the socket on unmount. Same-origin only — relies on the
// session cookie set by /api/v1/login for auth.
//
// The implementation uses a manual reconnect loop instead of React Query
// because a long-lived WebSocket is fundamentally not a query and the
// React Query primitive feels wrong here.
export function useWS({ sourceId, onEvent }: UseWSOptions = {}): UseWSResult {
  const [lastEvent, setLastEvent] = useState<WSEvent | null>(null);
  const [connected, setConnected] = useState(false);
  // Refs so the reconnect loop captures fresh values without re-arming the effect.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let cancelled = false;
    let backoffMs = 500;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      if (cancelled) return;
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const qs = sourceId ? `?sourceId=${encodeURIComponent(sourceId)}` : "";
      const url = `${proto}//${window.location.host}/api/v1/ws${qs}`;
      ws = new WebSocket(url);
      ws.addEventListener("open", () => {
        backoffMs = 500;
        setConnected(true);
      });
      ws.addEventListener("message", (e) => {
        try {
          const ev = JSON.parse(e.data) as WSEvent;
          setLastEvent(ev);
          onEventRef.current?.(ev);
        } catch {
          /* malformed event — ignore */
        }
      });
      ws.addEventListener("close", () => {
        setConnected(false);
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, backoffMs);
        backoffMs = Math.min(backoffMs * 2, 30_000);
      });
      ws.addEventListener("error", () => {
        // close fires after error; let it drive the reconnect.
        ws?.close();
      });
    }

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [sourceId]);

  return { lastEvent, connected };
}
